import { prefersReducedMotion } from '@theme/utilities';
import {
  pinProgress,
  inViewProgress,
  selectMode,
  headPosition,
  readAmount,
  charOpacity,
  DEFAULT_SOFTNESS,
} from '@theme/mission-reveal-math';

class MissionReveal extends HTMLElement {
  #track = null;
  #statement = null;
  #chars = [];
  #mode = 'pin';
  #softness = DEFAULT_SOFTNESS;
  #ticking = false;
  /** High-water mark — the reveal holds and never plays in reverse. */
  #peak = 0;
  #painted = -1;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    this.#statement = this.querySelector('[data-statement]');
    if (!this.#track || !this.#statement) return;

    if (prefersReducedMotion()) {
      this.dataset.mode = 'static';
      return;
    }

    this.#splitChars();
    this.#chooseMode();
    this.#render();

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
  }

  #splitChars() {
    const walker = document.createTreeWalker(this.#statement, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) textNodes.push(node);

    for (const textNode of textNodes) {
      const highlighted = Boolean(textNode.parentElement?.closest('em, i, [data-highlight]'));
      const fragment = document.createDocumentFragment();

      for (const ch of textNode.textContent) {
        if (ch === ' ' || ch === '\n' || ch === '\t') {
          fragment.appendChild(document.createTextNode(ch));
          continue;
        }
        const span = document.createElement('span');
        span.className = 'mission-reveal__char';
        span.textContent = ch;
        if (highlighted) span.dataset.highlight = '';
        fragment.appendChild(span);
      }

      textNode.replaceWith(fragment);
    }

    this.#chars = Array.from(this.querySelectorAll('.mission-reveal__char'));
    this.dataset.enhanced = '';
  }

  #chooseMode() {
    const height = this.#statement.getBoundingClientRect().height;
    this.#mode = selectMode(height, window.innerHeight);
    this.dataset.mode = this.#mode;
  }

  #render = () => {
    const viewportH = window.innerHeight;
    let progress;

    if (this.#mode === 'pin') {
      const rect = this.#track.getBoundingClientRect();
      progress = pinProgress(rect.top, rect.height, viewportH);
    } else {
      const rect = this.#statement.getBoundingClientRect();
      progress = inViewProgress(rect.top, rect.height, viewportH);
    }

    this.#peak = Math.max(this.#peak, progress);
    if (this.#peak === this.#painted) return;
    this.#painted = this.#peak;

    const head = headPosition(this.#peak, this.#chars.length, this.#softness);
    for (let i = 0; i < this.#chars.length; i++) {
      const amount = readAmount(i, head, this.#softness);
      this.#chars[i].style.opacity = charOpacity(amount).toFixed(3);
    }

    // Fully revealed — nothing left to animate, stop listening to scroll.
    if (this.#peak >= 1) {
      window.removeEventListener('scroll', this.#onScroll);
    }
  };

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#render();
      this.#ticking = false;
    });
  };

  #onResize = () => {
    this.#chooseMode();
    this.#render();
  };
}

if (!customElements.get('mission-reveal')) {
  customElements.define('mission-reveal', MissionReveal);
}
